/**
 * Operator-only recording start/stop with a double-confirm modal
 * (AGENTS.md §16: "Recording start requires fresh, in-session double-
 * confirmation"). Gated server-side by HasOrgPermission("session.recording.start"
 * / .stop"); the UI also gates so operators without the permission don't
 * see the control at all.
 *
 * Two extras handled here that surfaced in the mobile smoke:
 *   1. After /recording/start succeeds, broadcast `system/recording.started`
 *      so the field's ConsentHud shows the REC pill in real time.
 *      Symmetric for /stop.
 *   2. Render an HH:MM:SS elapsed timer while `status === "recording"`
 *      so the operator knows the egress is actually running. Anchored
 *      to the moment /start succeeded; if the operator refreshes the
 *      page mid-recording the timer restarts at 00:00:00 (the egress
 *      itself is unaffected — server-side state is the source of truth).
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RoomEvent } from "livekit-client";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { useConsentStore } from "@/lib/consent";
import { PermissionGate } from "@/components/PermissionGate";
import { useRecordingStore } from "@/lib/recording";
import { useSessionBus } from "@/lib/sessionBus";
import type { Session } from "@/lib/types";

type Props = {
  sessionId: string;
  status: Session["status"];
  /** Phase 1B-J — server-anchored start time. When present the badge
   *  + elapsed counter use this instead of `Date.now()` so a page
   *  refresh mid-recording resumes the right elapsed value. */
  serverRecordingStartedAt?: string | null;
};

export function RecordingControl({
  sessionId,
  status,
  serverRecordingStartedAt,
}: Props) {
  const recording = status === "recording";
  const paused = status === "paused";
  const active = recording || paused;

  // Phase 1B-I — bootstrap useRecordingStore from the session's
  // server-side status so a page refresh while a recording is in
  // flight rehydrates the local state without needing a bus envelope.
  //
  // Drives:
  //   - OperatorToolbar's Pause / Resume icon (gated on
  //     useRecordingStore.active)
  //   - ConsentHud's REC / PAUSED pill on both sides
  //   - The elapsed-time counter
  //
  // We don't (yet) persist startedAt on the server, so a refresh
  // restarts the visible counter at 00:00:00. The actual recording
  // duration is unaffected — server-side state is authoritative —
  // this is a UX-only counter. Tracked for a future bump that adds
  // `recording_started_at` to the Session serializer.
  const storeActive = useRecordingStore((s) => s.active);
  const storePaused = useRecordingStore((s) => s.paused);
  useEffect(() => {
    const store = useRecordingStore.getState();
    if (active && !storeActive) {
      // First mount or page-refresh — bootstrap. Prefer the server-
      // anchored start time (Phase 1B-J) so a refresh mid-recording
      // resumes the correct elapsed counter instead of restarting at
      // 00:00:00.
      const serverStartMs = serverRecordingStartedAt
        ? new Date(serverRecordingStartedAt).getTime()
        : null;
      const startMs = serverStartMs ?? Date.now();
      store.set(true, startMs);
      if (paused) store.pause(Date.now());
      // Also seed the module-level `recordingStartedAt` so the
      // late-join re-broadcast handler (above) uses the same anchor
      // a new field/observer who joins after this refresh will see.
      recordingStartedAt = startMs;
    } else if (active && storeActive && paused !== storePaused) {
      // Status drifted while mounted (e.g. another tab issued pause
      // out-of-band) — bring the store in line without resetting the
      // anchor.
      const now = Date.now();
      if (paused) store.pause(now);
      else store.resume(now);
    } else if (!active && storeActive) {
      // Server says we're no longer recording (operator stopped from
      // another tab or device) — drop the badge.
      store.reset();
      recordingStartedAt = null;
    }
  }, [active, paused, storeActive, storePaused, serverRecordingStartedAt]);
  // Late-join coverage: if a participant connects AFTER recording
  // started, they missed the `system/recording.started` envelope and
  // wouldn't show the REC badge. Re-broadcast every time someone new
  // joins the LiveKit room while we're still recording. Idempotent on
  // the receiving side — they just refresh the state.
  const bus = useSessionBus((s) => s.bus);
  const room = useSessionBus((s) => s.room);
  useEffect(() => {
    if (!room || !bus || !active) return;
    const onParticipantConnected = () => {
      void bus
        .send({
          topic: "system",
          type: paused ? "recording.paused" : "recording.started",
          payload: { at: recordingStartedAt ?? Date.now() },
        })
        .catch(() => {});
    };
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
    return () => {
      room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
    };
  }, [room, bus, active, paused]);

  return (
    <PermissionGate anyOf={["session.recording.start", "session.recording.stop"]}>
      <div className="flex flex-wrap items-center gap-2">
        <RecordingPill recording={recording} paused={paused} />
        {recording && (
          <>
            <PauseButton sessionId={sessionId} />
            <StopButton sessionId={sessionId} />
          </>
        )}
        {paused && (
          <>
            <ResumeButton sessionId={sessionId} />
            <StopButton sessionId={sessionId} />
          </>
        )}
        {!active && <StartButton sessionId={sessionId} />}
      </div>
    </PermissionGate>
  );
}

function RecordingPill({
  recording,
  paused,
}: {
  recording: boolean;
  paused: boolean;
}) {
  const { t } = useTranslation();
  if (paused) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-300">
        <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden />
        {t("ui.recordingControl.paused")}
        <ElapsedTime />
      </span>
    );
  }
  if (!recording) return <span className="pill text-nb-text-2">{t("ui.recordingControl.notRecording")}</span>;
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-cvp-danger/20 px-2 py-0.5 text-xs text-cvp-danger">
      <span className="h-2 w-2 animate-pulse rounded-full bg-cvp-danger" aria-hidden />
      {t("ui.recordingControl.rec")}
      <ElapsedTime />
    </span>
  );
}

/** HH:MM:SS counter rooted at the operator-side start time. */
function ElapsedTime() {
  const startedAt = useRecordingStartedAt();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!startedAt) return null;
  const sec = Math.max(0, Math.floor((now - startedAt) / 1000));
  return <span className="font-mono">{formatHMS(sec)}</span>;
}

function formatHMS(total: number): string {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}
function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

/** Tiny ref module so Start can publish a start time the Pill reads. */
let recordingStartedAt: number | null = null;
function useRecordingStartedAt(): number | null {
  // Re-render hook: a single ref + a state we increment on changes.
  const tick = useRef(0);
  const [, setN] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => {
      // No-op tick to ensure consistency across siblings; keeps this
      // hook pluggable if the start-time changes (e.g. a refresh while
      // the recording is still active — Phase 2 may persist this on
      // the Session row server-side and re-hydrate).
      tick.current += 1;
      setN(tick.current);
    }, 5000);
    return () => window.clearInterval(id);
  }, []);
  return recordingStartedAt;
}

function StartButton({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const setConsent = useConsentStore((s) => s.set);
  const bus = useSessionBus((s) => s.bus);
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();
  const start = useMutation({
    mutationFn: async () => {
      // Recording is consent-gated server-side (AGENTS.md §16). Backend
      // rejects /recording/start with 409 if session.consent_state_jsonb
      // doesn't carry recording=granted yet, so the operator's
      // double-confirm posts the consent first and only then starts the
      // egress. (Phase 2 will add field-side approval; for v1 the
      // operator's authority over the session is sufficient.)
      await api.post(ApiPaths.sessionConsent(sessionId), {
        kind: "recording",
        decision: "granted",
        note: "operator double-confirmed",
      });
      const res = await api.post<{ egress_id: string }>(
        ApiPaths.sessionRecordingStart(sessionId),
      );
      return res.data;
    },
    onSuccess: () => {
      setConsent("recording", "granted");
      recordingStartedAt = Date.now();
      // Lift into the cross-side store so ConsentHud's REC pill
      // renders the pulsing badge + elapsed-time on both sides.
      useRecordingStore.getState().set(true, recordingStartedAt);
      // Tell the field side to flip its REC pill on.
      void bus
        ?.send({
          topic: "system",
          type: "recording.started",
          payload: { at: recordingStartedAt },
        })
        .catch(() => {});
      void qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
    onError: (e) => {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      setErr(detail ?? (e as Error).message ?? t("ui.recordingControl.startError"));
    },
  });

  if (!confirming) {
    return (
      <div className="flex flex-col gap-1">
        <button className="btn-danger" onClick={() => { setErr(null); setConfirming(true); }}>
          {t("ui.recordingControl.startRecording")}
        </button>
        {err && (
          <p role="alert" className="text-xs text-cvp-danger">
            {err}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-xs text-cvp-warn">{t("ui.recordingControl.confirmPrompt")}</span>
        <button
          className="btn-danger"
          disabled={start.isPending}
          onClick={() => {
            start.mutate(undefined, {
              onSettled: () => setConfirming(false),
            });
          }}
        >
          {start.isPending ? t("ui.recordingControl.starting") : t("ui.recordingControl.confirm")}
        </button>
        <button className="btn-ghost" onClick={() => setConfirming(false)}>
          {t("ui.recordingControl.cancel")}
        </button>
      </div>
      {err && (
        <p role="alert" className="text-xs text-cvp-danger">
          {err}
        </p>
      )}
    </div>
  );
}

function StopButton({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const setConsent = useConsentStore((s) => s.set);
  const bus = useSessionBus((s) => s.bus);
  const stop = useMutation({
    mutationFn: async () => {
      const res = await api.post<Session>(ApiPaths.sessionRecordingStop(sessionId));
      return res.data;
    },
    onSuccess: () => {
      setConsent("recording", "revoked");
      recordingStartedAt = null;
      useRecordingStore.getState().reset();
      void bus
        ?.send({
          topic: "system",
          type: "recording.stopped",
          payload: {},
        })
        .catch(() => {});
      void qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
  });
  return (
    <button className="btn-ghost" onClick={() => stop.mutate()} disabled={stop.isPending}>
      {stop.isPending ? t("ui.recordingControl.stopping") : t("ui.recordingControl.stopRecording")}
    </button>
  );
}

// Mirrors backend settings.RECORDING_MIN_SEGMENT_SECONDS (default 3): the
// server rejects a pause until the current segment has run this long. The
// countdown below is a UX hint — the server stays authoritative (a too-fast
// pause still 409s and shows its message).
const PAUSE_MIN_SEGMENT_SECONDS = 3;


function PauseButton({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const bus = useSessionBus((s) => s.bus);
  const segmentStartedAt = useRecordingStore((s) => s.segmentStartedAt);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);
  const remaining = segmentStartedAt
    ? Math.max(
        0,
        Math.ceil(
          (PAUSE_MIN_SEGMENT_SECONDS * 1000 - (now - segmentStartedAt)) / 1000,
        ),
      )
    : 0;
  const pause = useMutation({
    mutationFn: async () => {
      const res = await api.post<Session>(
        ApiPaths.sessionRecordingPause(sessionId),
      );
      return res.data;
    },
    onSuccess: () => {
      const at = Date.now();
      useRecordingStore.getState().pause(at);
      void bus
        ?.send({
          topic: "system",
          type: "recording.paused",
          payload: { at },
        })
        .catch(() => {});
      void qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
  });
  // Surface the min-segment-duration guard (HTTP 409) so a too-fast pause
  // shows a clear message instead of silently doing nothing.
  const pauseError = pause.isError
    ? ((pause.error as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail ?? t("ui.recordingControl.pauseFailed"))
    : null;
  return (
    <div className="flex flex-col items-start gap-1">
      <button
        className="btn-ghost"
        onClick={() => pause.mutate()}
        disabled={pause.isPending || remaining > 0}
        data-testid="recording-pause"
      >
        {pause.isPending
          ? t("ui.recordingControl.pausing")
          : remaining > 0
            ? t("ui.recordingControl.pauseCountdown", { seconds: remaining })
            : t("ui.recordingControl.pause")}
      </button>
      {pauseError && (
        <p role="alert" className="text-xs text-cvp-danger">
          {pauseError}
        </p>
      )}
    </div>
  );
}

function ResumeButton({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const bus = useSessionBus((s) => s.bus);
  const resume = useMutation({
    mutationFn: async () => {
      const res = await api.post<Session>(
        ApiPaths.sessionRecordingResume(sessionId),
      );
      return res.data;
    },
    onSuccess: () => {
      const at = Date.now();
      useRecordingStore.getState().resume(at);
      void bus
        ?.send({
          topic: "system",
          type: "recording.resumed",
          payload: { at },
        })
        .catch(() => {});
      void qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
  });
  return (
    <button
      className="btn-danger"
      onClick={() => resume.mutate()}
      disabled={resume.isPending}
      data-testid="recording-resume"
    >
      {resume.isPending ? t("ui.recordingControl.resuming") : t("ui.recordingControl.resumeRecording")}
    </button>
  );
}